import React, { useEffect, useState } from 'react';
import { ArrowLeft, Clock, ArrowRight, Bag, Notes, Truck } from 'iconoir-react';
import { Offcanvas, Button } from 'react-bootstrap';
import Lottie from 'lottie-react';
import deliveryEmptyAnimation from '../assets/delivery-empty.json';
import { useCarrinho } from '../services/CarrinhoContext';
import { formatPrice } from '../utils/currency';
import useComandaPolling from '../hooks/useComandaPolling';
import LoginModal from './LoginModal';
import PedidoDetalhesLayout from './PedidoDetalhesLayout';
import { getComandasCliente, vincularComandaCliente } from '../services/api';
import './MeusPedidosOffcanvas.css';
import './ButtonStyles.css';


function MeusPedidosOffcanvas({ show, onHide }) {
  const { comandaExistente, ultimaComanda, setComandaExistente, setUltimaComanda, brand, mesa, mesaToken: contextMesaToken } = useCarrinho();
  const [usuario, setUsuario] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [comandasCliente, setComandasCliente] = useState([]);
  const [comandaSelecionada, setComandaSelecionada] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comandaDoBanco, setComandaDoBanco] = useState(null);
  
  // Comanda vem direto do banco, sem depender de contexto
  const comanda = comandaDoBanco;
  
  // Se usuário está logado mas não tem comanda local, buscar comandas do servidor
  const [precisaBuscarComandas, setPrecisaBuscarComandas] = useState(false);
  
  useEffect(() => {
    if (show && usuario && !comanda && comandasCliente.length === 0) {
      setPrecisaBuscarComandas(true);
    } else {
      setPrecisaBuscarComandas(false);
    }
  }, [show, usuario, comanda, comandasCliente.length]);

  // Debug - apenas quando relevante (removido para evitar logs excessivos)

  // Verificar se usuário está logado
  useEffect(() => {
    const token = localStorage.getItem('sitri_token');
    const user = localStorage.getItem('sitri_user');
    if (token && user) {
      try {
        const userData = JSON.parse(user);
        setUsuario(userData);
      } catch (error) {
        // Erro silencioso
      }
    } else {
      setUsuario(null);
      setComandasCliente([]);
      setComandaSelecionada(null);
    }

    // Ao abrir, SEMPRE buscar comanda da mesa direto no banco
    if (show && brand) {
      const buscarNoBanco = async () => {
        try {
          setLoading(true);
          // Extrair token da URL (/tablet/ ou /dinein/) ou do context
          const tabletMatch = window.location.pathname.match(/\/tablet\/([^/]+)/);
          const dineinMatch = window.location.pathname.match(/\/dinein\/([^/]+)/);
          const mesaToken = tabletMatch ? tabletMatch[1] : dineinMatch ? dineinMatch[1] : contextMesaToken;
          if (!mesaToken) { setLoading(false); return; }
          
          const { getComandaByMesa } = await import('../services/api');
          const resultado = await getComandaByMesa(brand, mesaToken);
          if (resultado && resultado.comanda && 
              resultado.comanda.status !== 'fechada' && 
              resultado.comanda.status !== 'entregue') {
            setComandaDoBanco(resultado.comanda);
          } else {
            setComandaDoBanco(null);
          }
        } catch (e) {
          setComandaDoBanco(null);
        } finally {
          setLoading(false);
        }
      };
      buscarNoBanco();
    }
  }, [show, brand, contextMesaToken]);

  // Recarregar quando pedido é finalizado
  useEffect(() => {
    const buscarNoBanco = async () => {
      if (!brand) return;
      const tabletMatch = window.location.pathname.match(/\/tablet\/([^/]+)/);
      const dineinMatch = window.location.pathname.match(/\/dinein\/([^/]+)/);
      const mesaToken = tabletMatch ? tabletMatch[1] : dineinMatch ? dineinMatch[1] : contextMesaToken;
      if (!mesaToken) return;
      
      try {
        const { getComandaByMesa } = await import('../services/api');
        const resultado = await getComandaByMesa(brand, mesaToken);
        if (resultado && resultado.comanda && 
            resultado.comanda.status !== 'fechada' && 
            resultado.comanda.status !== 'entregue') {
          setComandaDoBanco(resultado.comanda);
        }
      } catch (e) {}
    };

    window.addEventListener('pedidoFinalizado', buscarNoBanco);
    return () => window.removeEventListener('pedidoFinalizado', buscarNoBanco);
  }, [brand, contextMesaToken]);

  // Polling para buscar comandas do cliente quando logado e offcanvas aberto
  useEffect(() => {
    let intervalId;
    
    if (show && usuario) {
      const token = localStorage.getItem('sitri_token');
      if (token) {
        // Buscar imediatamente, especialmente se precisaBuscarComandas for true
        buscarComandasCliente(token, precisaBuscarComandas);
        intervalId = setInterval(() => {
          buscarComandasCliente(token, false);
        }, 30000);
      }
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [show, usuario, brand, precisaBuscarComandas]);


  
  // Função para buscar comandas do cliente
  const buscarComandasCliente = async (token, showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const result = await getComandasCliente(brand, token);
      if (result && result.success) {
        setComandasCliente(result.comandas || []);
      }
    } catch (error) {
      // Se erro 401, limpar token inválido
      if (error.response?.status === 401) {
        localStorage.removeItem('sitri_token');
        localStorage.removeItem('sitri_user');
        setUsuario(null);
        setComandasCliente([]);
      }
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  // Polling para monitorar atualizações da comanda (apenas quando offcanvas está aberto e não logado)
  const { isConnected } = useComandaPolling(comanda?.id, (comandaAtualizada) => {
    // Atualizar localStorage
    localStorage.setItem('sitri_ultima_comanda', JSON.stringify(comandaAtualizada));
    
    // Atualizar contexto apenas se realmente mudou
    if (comandaExistente && comandaExistente.id === comandaAtualizada.id) {
      setComandaExistente(comandaAtualizada);
    }
    if (ultimaComanda && ultimaComanda.id === comandaAtualizada.id) {
      setUltimaComanda(comandaAtualizada);
    }
  }, show && !usuario);

  const getStatusBadge = (status) => {
    const statusMap = {
      'aberta': { text: 'ANÁLISE', class: 'bg-warning text-dark' },
      'producao': { text: 'EM PREPARO', class: 'bg-primary text-light' },
      'pronta': { text: 'PRONTO', class: 'bg-primary text-light' },
      'fechada': { text: 'ENTREGUE', class: 'bg-success' },
      'entregue': { text: 'ENTREGUE', class: 'bg-success' },
      'caminho': { text: 'A CAMINHO', class: 'bg-primary' },
      'aguardando_pagamento': { text: 'PROCESSANDO', class: 'bg-warning text-dark' },
      'pagamento_confirmado': { text: 'PAGO', class: 'bg-success' },
      'cancelada': { text: 'CANCELADO', class: 'bg-danger' }
    
    };
    const normalizedStatus = (status || '').toLowerCase().replace(/[_\s-]/g, '_');
    const config = statusMap[normalizedStatus] || { text: (status || '').toUpperCase(), class: 'bg-success text-light' };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const getOrderTypeLabel = (cmd) => {
    if (cmd?.b_delivery) return 'delivery'
    if (cmd?.b_takeaway) return 'takeaway'
    if (cmd?.b_balcao) return 'balcao'
    return 'mesa'
  }

  const getOrderTypeBadge = (cmd) => {
    const type = getOrderTypeLabel(cmd)
    let color = '#6b7280'
    let Icon = Notes
    let label = 'mesa'
    if (type === 'delivery') { color = '#007bff'; Icon = Truck; label = 'DELIVERY' }
    else if (type === 'takeaway') { color = '#10b981'; Icon = Bag; label = 'TAKEAWAY' }
    else if (type === 'balcao') { color = '#6366f1'; Icon = Bag; label = 'BALÇÃO' }
    else { color = '#ec4899'; Icon = Notes; label = 'MESA' }
    return (
      <span
        className="ms-2"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '2px 8px',
          borderRadius: '9999px',
          border: `1px solid ${color}20`,
          backgroundColor: '#fff',
          color,
          fontSize: '0.75rem',
          fontWeight: 600
        }}
      >
        <Icon style={{ fontSize: '0.85rem' }} />
        {label}
      </span>
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Data inválida';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Data inválida';
    return date.toLocaleDateString('pt-BR') + ' - ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };



  return (
    <Offcanvas 
      show={show} 
      onHide={onHide} 
      placement="end" 
      style={{ width: "600px" }}
    >
      <Offcanvas.Header closeButton={!comandaSelecionada}>
        <Offcanvas.Title>
          <div className="d-flex align-items-center w-100" style={{gap:'8px'}}>
            {comandaSelecionada && (
              <Button 
                variant="outline-secondary" 
                onClick={() => setComandaSelecionada(null)}
                className="btn-cancel-light"
                style={{
                  padding: '8px 16px',
                  borderRadius: '12px',
                  fontSize: '0.85rem',
                  border: '1px solid #e5e7eb',
                  color: '#6b7280',
                  backgroundColor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <ArrowLeft width="16" height="16" />
                Voltar
              </Button>
            )}
            <h4 className="mb-0">
              {usuario 
                ? (comandaSelecionada 
                    ? `Pedido #${comandaSelecionada.id}` 
                    : 'Meus Pedidos'
                  )
                : (() => {
                    const pedidoId = comanda?.id || ultimaComanda?.id;
                    return pedidoId ? `Meus Pedidos - Pedido #${pedidoId}` : 'Meus Pedidos';
                  })()
              }
            </h4>
          </div>
        </Offcanvas.Title>
      </Offcanvas.Header>
      
      <Offcanvas.Body className="bg-light" style={{backgroundColor:'#f3f4f6'}}>
        <div className="container pt-3">
        {usuario ? (
          // Usuário logado - mostrar lista de comandas ou detalhe da comanda selecionada
          comandaSelecionada ? (
            // Detalhe da comanda selecionada
            <PedidoDetalhesLayout 
              comandaData={comandaSelecionada}
              showLoginPrompt={false}
              onComandaUpdate={(comandaAtualizada) => {
                setComandaSelecionada(comandaAtualizada);
                // Atualizar também na lista de comandas
                setComandasCliente(prev => 
                  prev.map(cmd => cmd.id === comandaAtualizada.id ? comandaAtualizada : cmd)
                );
              }}
            />
          ) : (
            // Lista de comandas do cliente
            <>
              {loading && comandasCliente.length === 0 ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <p className="mt-2">Carregando seus pedidos...</p>
                </div>
              ) : comandasCliente.length > 0 ? (
                <>
                  <h6 className="mb-3">
                    <Clock className="me-2" />
                    Seus Últimos Pedidos ({comandasCliente.length})
                  </h6>
                  
                  {comandasCliente.map((cmd) => {
                    // Usar valores da API se disponíveis
                    const valorTotal = cmd.valores?.total || cmd.pedidos?.reduce((total, pedido) => {
                      return total + (pedido.produtos?.reduce((subtotal, produto) => {
                        return subtotal + (parseFloat(produto.valor) * parseInt(produto.quantidade));
                      }, 0) || 0);
                    }, 0) || 0;
                    
                    const totalItens = cmd.pedidos?.reduce((total, pedido) => {
                      return total + (pedido.produtos?.length || 0);
                    }, 0) || 0;
                    
                    
                    
                    return (
                      <div
                        key={cmd.id}
                        onClick={() => setComandaSelecionada(cmd)}
                        className="d-flex align-items-center p-3"
                        style={{
                          background: '#fff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                          marginBottom: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        <div className="d-flex align-items-center flex-grow-1" style={{gap: '12px'}}>
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center">
                              <span className="fw-bold" style={{fontSize:'0.9rem', color:'#111827'}}>#{cmd.id}</span>
                              {getOrderTypeBadge(cmd)}
                            </div>
                            <div style={{color:'#6b7280', fontSize:'0.8rem'}}>
                              {formatDate(cmd.created_at)} • {totalItens} {totalItens === 1 ? 'item' : 'itens'}{cmd.nome_cliente ? ` • ${cmd.nome_cliente}` : ''}
                            </div>
                          </div>
                        </div>
                        <div className="text-end" style={{minWidth:'120px'}}>
                          <div className="fw-bold" style={{fontSize:'0.85rem', color:'#3f3f3fff'}}>{formatPrice(valorTotal)}</div>
                          <div style={{marginTop:'6px'}}>
                            {getStatusBadge(cmd.status)}
                          </div>
                        </div>
                      
                      </div>
                    );
                  })}
                </>
              ) : (
                <div className="text-center py-4">
                  <div style={{ width: '200px', height: '200px', margin: '0 auto 16px auto' }}>
                    <Lottie animationData={deliveryEmptyAnimation} loop={true} />
                  </div>
                  <div style={{ color: '#111827', fontWeight: 700, fontSize: '0.95rem', marginBottom: '8px' }}>Nenhum pedido encontrado</div>
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>Você ainda não fez nenhum pedido conosco.</div>
                </div>
              )}
            </>
          )
        ) : (
          // Usuário não logado - mostrar detalhe da comanda atual
          // Para balcão: comanda (do banco via mesaToken) é null, mas ultimaComanda tem o pedido recém feito
          (() => {
            const comandaParaMostrar = comanda || ultimaComanda;
            return comandaParaMostrar ? (
              <PedidoDetalhesLayout 
                comandaData={comandaParaMostrar}
                showLoginPrompt={true}
                onLoginClick={() => setShowLoginModal(true)}
                onComandaUpdate={(comandaAtualizada) => {
                  // Atualizar contexto
                  if (comandaExistente && comandaExistente.id === comandaAtualizada.id) {
                    setComandaExistente(comandaAtualizada);
                  }
                  if (ultimaComanda && ultimaComanda.id === comandaAtualizada.id) {
                    setUltimaComanda(comandaAtualizada);
                  }
                }}
              />
            ) : (
              <div className="text-center py-4">
                <div style={{ width: '200px', height: '200px', margin: '0 auto' }}>
                  <Lottie animationData={deliveryEmptyAnimation} loop={true} />
                </div>
                <div style={{ color: '#111827', fontWeight: 700, fontSize: '0.95rem' }}>Nenhum pedido encontrado</div>
                <div className="text-muted" style={{ fontSize: '0.85rem' }}>Faça seu primeiro pedido conosco!</div>
              </div>
            );
          })()
        )}
        </div>
      </Offcanvas.Body>
      
      {/* Modal de Login */}
      <LoginModal
        show={showLoginModal}
        onHide={() => setShowLoginModal(false)}
        onLogin={async (userData) => {
          setUsuario(userData);
          localStorage.setItem('sitri_user', JSON.stringify(userData));
          
          // Vincular comanda atual ao usuário recém logado/cadastrado
          if (comanda?.id && userData?.id) {
            try {
              const resultado = await vincularComandaCliente(brand, comanda.id, userData.id);
              if (resultado && resultado.success) {
                // Recarregar comandas do cliente para incluir a recém vinculada
                const token = localStorage.getItem('sitri_token');
                if (token) {
                  buscarComandasCliente(token);
                }
              } else {
              }
            } catch (error) {
            }
          }
        }}
        brandLogoUrl={window.lojaInfo?.logo_url}
      />

    </Offcanvas>
  );
}

export default MeusPedidosOffcanvas;
